import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import {
  AUDIT_REASON_MIN_LENGTH,
  type PaymentRailBrand,
  type PayoutProviderStatus,
} from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Payments → a rail's Brands: which brands offer the rail to their players, and the switch for one.
 *
 * THE WRITE:
 *   POST /api/payments/methods/:id/brands/:brandId   { enabled, reason }, behind platform.manage
 *
 * Until this, production could not offer a rail to a brand without SQL. A brand offers a rail when
 * its players see the rail on the deposit and withdraw screens; the switch reaches them at their
 * next request, and a request already made keeps its rail.
 *
 * ONE BRAND AT A TIME, and the dialog is titled by which way the chosen brand would move, so the
 * operator reads what the press does before making it. Taking a rail away is the warning tone
 * (`destructive-confirmations.test.ts` classifies it): nothing already requested is touched, no money
 * moves, and offering it again is this same control. Not dismissible, so a stray Escape cannot throw
 * away a reason already typed.
 *
 * ONLY A LIVE RAIL IS OFFERED TO ANYBODY NEW — the route refuses the rest, and the dialog says so
 * before the press rather than after it. Taking one away is allowed whatever its status.
 *
 * THE STARPAY RAIL WAITS FOR A PAYOUT PROVIDER. It is paid by the platform's own hand-over, so with
 * no payout provider configured (the OFF driver) the player's list of rails leaves it out and a
 * request naming it is refused (`railOfferedForWithdrawal` in the API). Offered then, the brand's
 * players see it on withdraw screens once a provider is configured, not from now on, and the notice
 * says so (`SwitchWords`). The Payments screen reads which driver is on and hands it in.
 */

/** The rail as the Payments screen read it: what this dialog needs of the row. */
export interface RailForBrands {
  id: string;
  name: string;
  /** Who carries the rail's money, as the rail names it: STARPAY is the payout provider's rail. */
  provider: string;
  /** LIVE · DEGRADED · DISABLED */
  status: string;
  supports_deposit: boolean;
  supports_withdrawal: boolean;
  /** Every active brand in the reader's scope, and whether it offers the rail. */
  brands: PaymentRailBrand[];
}

/** Which payout driver is on, as the provider's status names it; null while it is not known. */
type PayoutDriver = PayoutProviderStatus['driverName'];

/** Which way the chosen brand would move: its title, the press, and the tone of what it means. */
export const RAIL_BRAND_SWITCH = {
  on: {
    title: (rail: string, brand: string) => `Offer ${rail} to ${brand}?`,
    button: 'Offer it',
    tone: 'quiet',
  },
  off: {
    title: (rail: string, brand: string) => `Remove ${rail} from ${brand}?`,
    button: 'Remove it',
    tone: 'warn',
  },
} as const;

const STATUS_WORDS: Record<string, string> = { LIVE: 'live', DEGRADED: 'degraded', DISABLED: 'disabled' };

/** The screens a rail appears on for a player, by the directions it carries: a rail that only pays
    out is never on a deposit screen, and the sentence does not say it is. */
const screensOf = (r: Pick<RailForBrands, 'supports_deposit' | 'supports_withdrawal'>): string =>
  r.supports_deposit && r.supports_withdrawal
    ? 'deposit and withdraw screens'
    : r.supports_deposit
      ? 'deposit screens'
      : 'withdraw screens';

/**
 * WHAT THE PRESS MEANS FOR THE BRAND'S PLAYERS, in the notice beside the button.
 *
 * Offering the StarPay rail with no payout provider configured shows the brand's players nothing on
 * withdraw screens until one is, so the sentence says that rather than "from now on". Said only when
 * the screen knows the driver: while its read has not answered (or failed), `payoutDriver` is null
 * and the sentence is the one for a deployment that sends.
 */
export function SwitchWords({
  to,
  rail,
  brandName,
  payoutDriver,
}: {
  to: keyof typeof RAIL_BRAND_SWITCH;
  rail: Pick<RailForBrands, 'provider' | 'supports_deposit' | 'supports_withdrawal'>;
  brandName: string;
  payoutDriver: PayoutDriver | null;
}) {
  if (to === 'off') {
    return (
      <>
        Players on {brandName} no longer see it; nothing already requested is touched. Offering it
        again is this same control.
      </>
    );
  }
  const waitsForProvider =
    rail.provider === 'STARPAY' && rail.supports_withdrawal && payoutDriver === 'OFF';
  if (!waitsForProvider) {
    return (
      <>
        Players on {brandName} see this rail on {screensOf(rail)} from now on.
      </>
    );
  }
  return rail.supports_deposit ? (
    <>
      Players on {brandName} see this rail on deposit screens from now on, and on withdraw screens
      once a payout provider is configured.
    </>
  ) : (
    <>
      Players on {brandName} see this rail on withdraw screens once a payout provider is configured.
    </>
  );
}

export function RailBrandsDialog({
  rail,
  payoutDriver,
  onClose,
  onChanged,
}: {
  /** The rail whose brands are being set, or null when the dialog is closed. */
  rail: RailForBrands | null;
  /** Which payout driver is on, as the Payments screen read it; null until that read has answered. */
  payoutDriver: PayoutDriver | null;
  onClose: () => void;
  /** After a brand was switched, so the rail list is read again. */
  onChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [brandId, setBrandId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  /* Every opening starts clean: a reason typed for one rail must never be sent against another.
     Adjusted during render rather than in an effect, as the screen does elsewhere. */
  const [openedFor, setOpenedFor] = useState<string | null>(rail?.id ?? null);
  if (openedFor !== (rail?.id ?? null)) {
    setOpenedFor(rail?.id ?? null);
    setBrandId(null);
    setReason('');
    setReasonError(undefined);
  }

  const brands = rail?.brands ?? [];
  const chosen = brands.find((b) => b.brandId === brandId) ?? brands[0] ?? null;
  const to: keyof typeof RAIL_BRAND_SWITCH = chosen?.enabled ? 'off' : 'on';
  /* Offering a rail that is not live is refused by the route; said here, before the press. */
  const notLive = rail !== null && to === 'on' && rail.status !== 'LIVE';
  const trimmed = reason.trim();
  const tooShort =
    reason.length > 0 && trimmed.length < AUDIT_REASON_MIN_LENGTH
      ? `Say why this brand’s players gain or lose this rail — at least ${AUDIT_REASON_MIN_LENGTH} characters.`
      : undefined;
  const ready =
    chosen !== null &&
    !notLive &&
    trimmed.length >= AUDIT_REASON_MIN_LENGTH &&
    trimmed.length <= 500;

  const apply = async () => {
    if (rail === null || chosen === null || !ready || busy) return;
    setBusy(true);
    try {
      const result = await api.post<{ effect: string }>(
        `/api/payments/methods/${encodeURIComponent(rail.id)}/brands/${encodeURIComponent(chosen.brandId)}`,
        { enabled: to === 'on', reason: trimmed },
      );
      /* The route's own sentence: what the brand's players see from now on. */
      toast(result.effect);
      onChanged();
      onClose();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['reason']) {
        setReasonError(error.fields['reason']);
        return;
      }
      showError({
        title:
          to === 'off'
            ? `${rail.name} was not removed from ${chosen.brandName}`
            : `${rail.name} was not offered to ${chosen.brandName}`,
        message: error instanceof ApiRequestError ? error.message : 'Nothing was changed on this rail.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={rail !== null}
      onClose={onClose}
      title={rail === null || chosen === null ? 'Brands that offer this rail' : RAIL_BRAND_SWITCH[to].title(rail.name, chosen.brandName)}
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!ready || busy} onClick={() => void apply()}>
            {RAIL_BRAND_SWITCH[to].button}
          </Button>
        </>
      }
    >
      {rail === null ? null : chosen === null ? (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            No active brand is in your scope, so there is no brand here to offer {rail.name} to.
          </div>
        </div>
      ) : (
        <div className="stack">
          <dl className="stack" style={{ margin: 0 }}>
            {brands.map((b) => (
              <div className="def-row" key={b.brandId}>
                <dt>{b.brandName}</dt>
                <dd>
                  <Tag tone={b.enabled ? 'accent' : 'quiet'}>{b.enabled ? 'Offered' : 'Not offered'}</Tag>
                </dd>
              </div>
            ))}
          </dl>

          <Field label="Brand to switch" hint="One brand at a time. Its players see the change at their next request.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={chosen.brandId}
                onChange={(e) => {
                  setBrandId(e.target.value);
                  setReasonError(undefined);
                }}
              >
                {brands.map((b) => (
                  <option key={b.brandId} value={b.brandId}>
                    {b.brandName} — {b.enabled ? 'offered now; switch it off' : 'not offered now; switch it on'}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/* What the press means for the brand's players, said beside the button. Warn when the
              rail is taken away, quiet when it is offered. */}
          <div className={RAIL_BRAND_SWITCH[to].tone === 'warn' ? 'notice notice-warn' : 'notice notice-quiet'}>
            <Icon name={to === 'off' ? 'warning' : 'info'} />
            <div>
              <SwitchWords
                to={to}
                rail={rail}
                brandName={chosen.brandName}
                payoutDriver={payoutDriver}
              />
            </div>
          </div>

          {notLive ? (
            <div className="notice">
              <Icon name="warning-circle" />
              <div>
                {rail.name} is {STATUS_WORDS[rail.status] ?? 'not live'}, so it is offered to nobody new.
                Set the rail to Live first, then offer it here.
              </div>
            </div>
          ) : null}

          <Field
            label="Reason"
            hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters, kept on the audit trail with your name — what the next operator reads when a brand's players ask where a rail went.`}
            error={reasonError ?? tooShort}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                minLength={AUDIT_REASON_MIN_LENGTH}
                maxLength={500}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonError(undefined);
                }}
                placeholder="UAT rows filled in on 12 Oct; StarPay confirmed the merchant is live."
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
